import { useState, useEffect, useMemo } from 'react'
import { Folder, ChevronRight, ChevronDown } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { TreeData } from '@/components/ui/tree-view'
import { fetchWithAuth } from '@/lib/api'
import {
  displaySegmentsForStoragePath,
  type WorkspaceRouteInfo,
} from '@/lib/app-routes'

export type WorkspaceOption = {
  id: string
  name: string
  contentRoot: string
  /** Optional slug for display paths (ws/{hex} -> human labels). */
  slug?: string
}

interface FolderPickerDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentPath: string // Current document or folder path
  onSelect: (folderPath: string) => void
  /** When 'folder', title/description reflect moving a folder (and all its documents) */
  mode?: 'file' | 'folder' | 'import' | 'create'
  /** Override dialog title */
  title?: string
  /** Override dialog description */
  description?: string
  /** Override confirm button label */
  confirmLabel?: string
  /** When provided, show a destination workspace dropdown and scope tree to selected workspace */
  workspaces?: WorkspaceOption[]
}

function normalizeStoragePath(p: string): string {
  return (p || '').replace(/^\/+/, '').replace(/\/+$/, '')
}

/** Keep picker selection inside the active workspace root (prevents cross-workspace Move to labels). */
export function clampPathToContentRoot(path: string, contentRoot: string): string {
  const root = normalizeStoragePath(contentRoot)
  if (!root) return ''
  const selected = normalizeStoragePath(path)
  if (!selected) return root
  if (selected === root || selected.startsWith(`${root}/`)) return selected
  return root
}

/** Human path for Current / Move to (hides ws/{hex}; uses workspace name at root). */
function formatPickerDisplayPath(
  storagePath: string,
  workspaces: WorkspaceRouteInfo[],
  fallbackRootLabel: string,
  /** When set, never resolve a path that belongs to another workspace. */
  contentRoot?: string
): string {
  const normalized = contentRoot
    ? clampPathToContentRoot(storagePath, contentRoot)
    : normalizeStoragePath(storagePath)
  if (!normalized) return fallbackRootLabel
  const segments = displaySegmentsForStoragePath(normalized, workspaces)
  if (segments.length === 0) return fallbackRootLabel
  return segments.map((s) => s.label).join('/')
}

/** Explorer-tree with ?root= returns the root as a single wrapper node - unwrap like Browse. */
function unwrapExplorerTree(tree: TreeData[], contentRoot: string): TreeData[] {
  const root = normalizeStoragePath(contentRoot)
  if (
    tree.length === 1 &&
    tree[0]?.type === 'folder' &&
    normalizeStoragePath(String(tree[0].path ?? tree[0].id ?? '')) === root
  ) {
    return (tree[0].children ?? []).filter((n) => n.type === 'folder')
  }
  return tree.filter((n) => n.type === 'folder')
}

export function FolderPickerDialog({
  open,
  onOpenChange,
  currentPath,
  onSelect,
  mode = 'file',
  title: titleProp,
  description: descriptionProp,
  confirmLabel: confirmLabelProp,
  workspaces: workspacesProp = [],
}: FolderPickerDialogProps) {
  const [folders, setFolders] = useState<TreeData[]>([])
  const [selectedPath, setSelectedPath] = useState<string>('')
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(false)

  const workspaces = workspacesProp
  const showWorkspaceSelector = workspaces.length > 1

  const routeWorkspaces: WorkspaceRouteInfo[] = useMemo(
    () =>
      workspaces.map((w) => ({
        id: w.id,
        slug: w.slug || w.id,
        name: w.name,
        contentRoot: w.contentRoot,
      })),
    [workspaces]
  )

  const defaultWorkspaceId = useMemo(() => {
    if (!currentPath || workspaces.length === 0) return workspaces[0]?.id ?? ''
    const normalized = normalizeStoragePath(currentPath)
    const match = workspaces.find((w) => {
      const root = normalizeStoragePath(w.contentRoot || '')
      return root && (normalized === root || normalized.startsWith(root + '/'))
    })
    return match?.id ?? workspaces[0]?.id ?? ''
  }, [currentPath, workspaces])

  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string>(defaultWorkspaceId)

  const selectedWorkspace = useMemo(
    () => workspaces.find((w) => w.id === selectedWorkspaceId) ?? workspaces[0],
    [workspaces, selectedWorkspaceId]
  )
  const contentRoot = normalizeStoragePath(selectedWorkspace?.contentRoot ?? '')
  const hasContentRoot = contentRoot.length > 0
  const rootLabel = selectedWorkspace?.name?.trim() || 'Workspace root'

  const handleWorkspaceChange = (id: string) => {
    setSelectedWorkspaceId(id)
    const next = workspaces.find((w) => w.id === id)
    const nextRoot = normalizeStoragePath(next?.contentRoot ?? '')
    // Drop prior workspace tree/selection immediately so labels never cross-wire.
    setFolders([])
    setExpandedNodes(new Set())
    setSelectedPath(nextRoot)
    setLoading(Boolean(nextRoot))
  }

  useEffect(() => {
    if (!open) return
    if (defaultWorkspaceId) setSelectedWorkspaceId(defaultWorkspaceId)
  }, [open, defaultWorkspaceId])

  useEffect(() => {
    if (!open) return

    if (!hasContentRoot) {
      setFolders([])
      setSelectedPath('')
      setExpandedNodes(new Set())
      setLoading(false)
      return
    }

    // Optimistic clamp before fetch so Move to never shows another workspace.
    setSelectedPath((prev) => clampPathToContentRoot(prev, contentRoot))
    setFolders([])
    setLoading(true)

    let cancelled = false
    const fetchFolders = async () => {
      try {
        // Always scope to the workspace content root (even with a single workspace).
        const url = `/api/documents/explorer-tree?root=${encodeURIComponent(contentRoot)}`
        const response = await fetchWithAuth(url)
        const data = await response.json()
        if (cancelled) return
        if (data.success) {
          setFolders(unwrapExplorerTree(data.tree ?? [], contentRoot))
          expandToPath(currentPath, contentRoot, mode === 'create')
        } else {
          setFolders([])
          setSelectedPath(contentRoot)
          setExpandedNodes(new Set())
        }
      } catch (error) {
        console.error('Failed to fetch folder tree:', error)
        if (!cancelled) {
          setFolders([])
          setSelectedPath(contentRoot)
          setExpandedNodes(new Set())
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void fetchFolders()
    return () => {
      cancelled = true
    }
  }, [open, currentPath, contentRoot, hasContentRoot, mode])

  const expandToPath = (fileOrFolderPath: string, root: string, pathIsFolder = false) => {
    if (!fileOrFolderPath) {
      setSelectedPath(root)
      setExpandedNodes(new Set())
      return
    }
    const normalized = normalizeStoragePath(fileOrFolderPath)
    // Create mode: currentPath is already the folder. Move/import: parent of the file.
    const folderPath = pathIsFolder
      ? normalized
      : normalized.split('/').slice(0, -1).join('/')
    const destination =
      folderPath && (folderPath === root || folderPath.startsWith(root + '/'))
        ? folderPath
        : root
    const parts = destination.split('/').filter(Boolean)
    const expanded = new Set<string>()
    let current = ''
    for (const part of parts) {
      current = current ? `${current}/${part}` : part
      expanded.add(current)
    }
    setExpandedNodes(expanded)
    setSelectedPath(destination)
  }

  const handleFolderSelect = (folderPath: string) => {
    setSelectedPath(clampPathToContentRoot(folderPath, contentRoot))
  }

  const fullSelectedPath = useMemo(() => {
    if (!hasContentRoot) return ''
    return clampPathToContentRoot(selectedPath, contentRoot)
  }, [hasContentRoot, contentRoot, selectedPath])

  const displayCurrent = useMemo(() => {
    if (mode === 'import' || mode === 'create') return ''
    return formatPickerDisplayPath(currentPath, routeWorkspaces, rootLabel)
  }, [mode, currentPath, routeWorkspaces, rootLabel])

  const displayMoveTo = useMemo(
    () => formatPickerDisplayPath(fullSelectedPath, routeWorkspaces, rootLabel, contentRoot),
    [fullSelectedPath, routeWorkspaces, rootLabel, contentRoot]
  )

  const handleMove = () => {
    if (fullSelectedPath !== '' && hasContentRoot) onSelect(fullSelectedPath)
  }

  const dialogTitle =
    titleProp ??
    (mode === 'folder'
      ? 'Move folder'
      : mode === 'import'
        ? 'Import to Maater'
        : mode === 'create'
          ? 'New document location'
          : 'Move document')
  const dialogDescription =
    descriptionProp ??
    (mode === 'folder'
      ? 'Select a destination. All documents in this folder will be moved.'
      : mode === 'import'
        ? 'Choose a folder in Maater to import this file into.'
        : mode === 'create'
          ? 'Choose a folder for the new document.'
          : 'Select a folder to move the document to')
  const dialogConfirmLabel =
    confirmLabelProp ??
    (mode === 'import' ? 'Import Here' : mode === 'create' ? 'Create Here' : 'Move Here')

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-card border-border">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogDescription>{dialogDescription}</DialogDescription>
        </DialogHeader>

        {showWorkspaceSelector && (
          <div className="space-y-2">
            <Label>Destination workspace</Label>
            <Select
              value={selectedWorkspaceId || undefined}
              onValueChange={handleWorkspaceChange}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select workspace" />
              </SelectTrigger>
              <SelectContent>
                {workspaces.map((w) => (
                  <SelectItem key={w.id} value={w.id}>
                    {w.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {mode !== 'import' && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Current:</span>
            <code className="px-2 py-1 bg-muted rounded text-xs break-all">{displayCurrent}</code>
          </div>
        )}

        {!hasContentRoot ? (
          <div className="h-[400px] flex flex-col items-center justify-center gap-2 rounded-md border border-border bg-muted/30 p-6 text-center text-sm text-muted-foreground">
            <p>This workspace has no content folder.</p>
            <p>
              Add one in <strong>Settings → Organizations</strong> to move items here.
            </p>
          </div>
        ) : loading ? (
          <div className="h-[400px] flex items-center justify-center">
            <p className="text-sm text-muted-foreground">Loading folders...</p>
          </div>
        ) : (
          <ScrollArea className="h-[400px] rounded-md border border-border p-4">
            <FolderTree
              data={folders}
              selectedPath={fullSelectedPath}
              contentRoot={contentRoot}
              rootLabel={rootLabel}
              expandedNodes={expandedNodes}
              onSelect={handleFolderSelect}
              onToggle={(nodeId) => {
                const newExpanded = new Set(expandedNodes)
                if (newExpanded.has(nodeId)) {
                  newExpanded.delete(nodeId)
                } else {
                  newExpanded.add(nodeId)
                }
                setExpandedNodes(newExpanded)
              }}
            />
          </ScrollArea>
        )}

        <div className="p-3 bg-muted rounded-md">
          <div className="text-sm font-medium mb-1">
            {mode === 'import' ? 'Import to:' : mode === 'create' ? 'Create in:' : 'Move to:'}
          </div>
          <code className="text-xs break-all">{displayMoveTo}</code>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleMove} disabled={!hasContentRoot || fullSelectedPath === ''}>
            {dialogConfirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface FolderTreeProps {
  data: TreeData[]
  selectedPath: string
  contentRoot: string
  rootLabel: string
  expandedNodes: Set<string>
  onSelect: (path: string) => void
  onToggle: (nodeId: string) => void
}

function FolderTree({
  data,
  selectedPath,
  contentRoot,
  rootLabel,
  expandedNodes,
  onSelect,
  onToggle,
}: FolderTreeProps) {
  const rootSelected = normalizeStoragePath(selectedPath) === normalizeStoragePath(contentRoot)
  return (
    <div className="space-y-1">
      <div
        className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-muted transition-colors ${
          rootSelected ? 'bg-primary/10 border border-primary' : ''
        }`}
        onClick={() => onSelect(contentRoot)}
      >
        <Folder className="h-4 w-4 text-yellow-500" />
        <span className="text-sm">{rootLabel}</span>
      </div>
      {data.map((node) => (
        <FolderTreeNode
          key={node.id}
          node={node}
          selectedPath={selectedPath}
          expandedNodes={expandedNodes}
          onSelect={onSelect}
          onToggle={onToggle}
          level={0}
        />
      ))}
    </div>
  )
}

function FolderTreeNode({
  node,
  selectedPath,
  expandedNodes,
  onSelect,
  onToggle,
  level,
}: {
  node: TreeData
  selectedPath: string
  expandedNodes: Set<string>
  onSelect: (path: string) => void
  onToggle: (nodeId: string) => void
  level: number
}) {
  const isExpanded = expandedNodes.has(node.id)
  const folderPath = node.path || node.id
  const isSelected = normalizeStoragePath(selectedPath) === normalizeStoragePath(folderPath)
  const hasChildren = node.children && node.children.some((c) => c.type === 'folder')

  if (node.type !== 'folder') return null

  return (
    <div>
      <div
        className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-muted transition-colors ${
          isSelected ? 'bg-primary/10 border border-primary' : ''
        }`}
        style={{ paddingLeft: `${level * 16 + 8}px` }}
        onClick={() => onSelect(folderPath)}
      >
        {hasChildren && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onToggle(node.id)
            }}
            className="p-0.5 hover:bg-muted rounded"
          >
            {isExpanded ? (
              <ChevronDown className="h-3 w-3" />
            ) : (
              <ChevronRight className="h-3 w-3" />
            )}
          </button>
        )}
        {!hasChildren && <div className="w-4" />}
        <Folder className="h-4 w-4 text-yellow-500" />
        <span className="text-sm">{node.name}</span>
      </div>
      {hasChildren && isExpanded && (
        <div>
          {node.children
            ?.filter((child) => child.type === 'folder')
            .map((child) => (
              <FolderTreeNode
                key={child.id}
                node={child}
                selectedPath={selectedPath}
                expandedNodes={expandedNodes}
                onSelect={onSelect}
                onToggle={onToggle}
                level={level + 1}
              />
            ))}
        </div>
      )}
    </div>
  )
}
